"use client"

import { useEffect, useRef, useCallback } from "react"
import { PropertyCard } from "@/components/property-card"
import type { Property } from "@/types/api"

interface VirtualizedPropertyGridProps {
  properties: Property[]
  loading: boolean
  loadingMore: boolean
  hasNextPage: boolean
  onLoadMore: () => void
  showMap: boolean
}

export function VirtualizedPropertyGrid({
  properties,
  loading,
  loadingMore,
  hasNextPage,
  onLoadMore,
  showMap,
}: VirtualizedPropertyGridProps) {
  const observerRef = useRef<IntersectionObserver | null>(null)
  const loadMoreRef = useRef<HTMLDivElement | null>(null)

  // Intersection Observer for infinite scroll
  const lastPropertyElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading || loadingMore) return

      if (observerRef.current) {
        observerRef.current.disconnect()
      }

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage) {
            onLoadMore()
          }
        },
        {
          threshold: 0.1,
          rootMargin: "100px", // Load more when 100px away from the element
        },
      )

      if (node) {
        observerRef.current.observe(node)
      }
    },
    [loading, loadingMore, hasNextPage, onLoadMore],
  )

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [])

  // Grid configuration based on map visibility
  const gridClasses = showMap
    ? "grid-cols-1 md:grid-cols-2" // 2 columns when map is shown
    : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" // 4 columns when map is hidden

  return (
    <div className="space-y-6">
      <div className={`grid gap-6 ${gridClasses}`}>
        {properties.map((property, index) => {
          // Add ref to the last few items to trigger loading
          const isLastItem = index === properties.length - 1
          const isNearEnd = index >= properties.length - 3

          return (
            <div
              key={`${property.id}-${index}`}
              ref={isLastItem ? lastPropertyElementRef : null}
              className={isNearEnd ? "animate-in fade-in-50 duration-300" : ""}
            >
              <PropertyCard property={property} compact={showMap} />
            </div>
          )
        })}
      </div>

      {/* Loading More Indicator */}
      {loadingMore && (
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-raime-golden-brown"></div>
            <span className="text-muted-foreground">Cargando más propiedades...</span>
          </div>
        </div>
      )}

      {/* End of Results */}
      {!hasNextPage && properties.length > 0 && (
        <div className="text-center py-8">
          <div className="inline-flex items-center px-4 py-2 bg-gray-100 rounded-full">
            <span className="text-sm text-muted-foreground">✨ Has visto todas las propiedades disponibles</span>
          </div>
        </div>
      )}

      {/* Invisible trigger element for better UX */}
      <div ref={loadMoreRef} className="h-4" />
    </div>
  )
}
